import { Head, router } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { destinationPath, destinationsPath } from "@travily/routes";
import type DestinationsShow from "@travily/types/pages/DestinationsShow";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useState } from "react";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import { type Household, REFERENCE_HOUSEHOLD } from "./apply-household";
import { ShowView } from "./show.view";

const DestinationShowPage = ({ costs, destination }: DestinationsShow) => {
  const onBack = useNavigateBack(destinationsPath());
  // The household the reader asked for, which leads the server's answer for as
  // long as the re-quote takes. Null until they change one: until then the two
  // agree, and the page has no opinion of its own to hold.
  const [asked, setAsked] = useState<Household | null>(null);
  const household = asked ?? costs?.household ?? REFERENCE_HOUSEHOLD;

  const quote = (next: Household) => {
    setAsked(next);
    router.get(
      destinationPath(destination.country_slug, destination.slug),
      { adults: next.adults, child_ages: next.child_ages, nights: next.nights, tier: next.tier },
      {
        only: ["costs"],
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onFinish: () => setAsked(null),
      },
    );
  };

  return (
    <PageFrame stickyTopBar={false} mobileTopBar={<TopBarMobile onBack={onBack} title={destination.name} />}>
      <Head title={t("web.destinations.show.meta_title", { destination: destination.name })}>
        <meta
          name="description"
          content={
            destination.marketing_sentence ??
            t("web.destinations.show.meta_description", { destination: destination.name })
          }
        />
      </Head>
      <ShowView
        costs={costs}
        destination={destination}
        household={household}
        isQuoting={asked !== null}
        onHouseholdChange={quote}
      />
    </PageFrame>
  );
};

export default DestinationShowPage;
